/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Día 1: CTA + camino. `embedded` = soft dentro de la hoja anclada (proto densidad).
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { VueltiFeliz } from '../../shared/ui/VueltiFeliz';
import { trackOnboardingFunnel } from '../application/onboardingFunnel';
import type { Day1Phase, Day1Step, Day1StepId } from '../application/resolveDay1Path';

const BRAND = ticketColors.brandPrimary;
const BRAND_DEEP = ticketColors.brandDeep;
const BRAND_SOFT = ticketColors.brandSoft;
const BRAND_INK = ticketColors.brandInk;
const ON_BRAND = ticketColors.goldInk;

export type Day1NextCardCopy = {
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  afterHint?: string;
  dismissLabel?: string;
  /** Título del bloque Camino (primera venta vs primer turno). */
  pathTitle?: string;
};

type Props = {
  phase: Day1Phase;
  stepIndex: number;
  stepTotal: number;
  copy: Day1NextCardCopy;
  onPrimary: () => void;
  onDismiss?: () => void;
  steps: Day1Step[];
  stepLabels: Record<Day1StepId, { label: string; hint: string }>;
  showPathList: boolean;
  /** Dentro de HomeDay1Sheet: soft teal, path sin card aparte. */
  embedded?: boolean;
};

export function Day1NextCard({
  phase,
  stepIndex,
  stepTotal,
  copy,
  onPrimary,
  onDismiss,
  steps,
  stepLabels,
  showPathList,
  embedded = false,
}: Props) {
  const celebrate =
    phase === 'celebrate_first_sale' || phase === 'celebrate_first_appointment';
  const useSoft = embedded && !celebrate;
  const bg = celebrate ? BRAND_DEEP : useSoft ? BRAND_SOFT : BRAND;

  return (
    <View style={[styles.wrap, useSoft && styles.wrapEmbedded]}>
      <View
        style={[
          styles.card,
          useSoft && styles.cardSoft,
          { backgroundColor: bg },
          useSoft && { borderColor: ticketColors.brandBorderSoft, borderWidth: 1 },
        ]}
        testID="home.day1.next"
      >
        {celebrate ? (
          <VueltiFeliz size={72} testID="home.day1.vuelti" />
        ) : null}
        <Text style={[styles.eyebrow, useSoft && styles.eyebrowSoft]}>{copy.eyebrow}</Text>
        <Text style={[styles.title, useSoft && styles.titleSoft]}>{copy.title}</Text>
        <Text style={[styles.body, useSoft && styles.bodySoft]}>{copy.body}</Text>
        <TouchableOpacity
          style={[styles.cta, useSoft && styles.ctaSoft]}
          onPress={() => {
            trackOnboardingFunnel('day1_cta_clicked', { phase });
            onPrimary();
          }}
          accessibilityRole="button"
          accessibilityLabel={copy.cta}
          testID="home.day1.cta"
        >
          <Text style={[styles.ctaLabel, useSoft && styles.ctaLabelSoft]}>{copy.cta}</Text>
          <Ionicons name="arrow-forward" size={16} color={useSoft ? ON_BRAND : BRAND} />
        </TouchableOpacity>
        {copy.afterHint && !celebrate ? (
          <Text style={[styles.after, useSoft && styles.afterSoft]}>{copy.afterHint}</Text>
        ) : null}
        {celebrate && onDismiss ? (
          <TouchableOpacity
            onPress={onDismiss}
            accessibilityRole="button"
            accessibilityLabel={copy.dismissLabel ?? 'Ocultar esta ayuda'}
            style={styles.dismissBtn}
            testID="home.day1.dismiss"
          >
            <Text style={styles.dismissLabel}>{copy.dismissLabel ?? 'Ocultar esta ayuda'}</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {showPathList ? (
        <View style={[styles.path, useSoft && styles.pathEmbedded]} testID="home.day1.path">
          {!useSoft ? (
            <Text style={styles.pathTitle}>{copy.pathTitle ?? 'Camino a la primera venta'}</Text>
          ) : null}
          {steps.map((s, i) => {
            if (s.status === 'skipped') return null;
            const meta = stepLabels[s.id];
            const visibleSteps = steps.filter((x) => x.status !== 'skipped');
            const num = visibleSteps.indexOf(s) + 1;
            const done = s.status === 'done';
            const current = s.status === 'current';
            const isFirstVisible = visibleSteps[0]?.id === s.id;
            return (
              <View
                key={s.id}
                style={[
                  styles.pathRow,
                  useSoft && styles.pathRowEmbedded,
                  useSoft && isFirstVisible && styles.pathRowEmbeddedFirst,
                ]}
              >
                <View style={[styles.check, done && styles.checkDone, current && styles.checkCurrent]}>
                  <Text style={[styles.checkNum, done && styles.checkNumDone, current && !done && styles.checkNumOn]}>
                    {done ? '✓' : String(num || i + 1)}
                  </Text>
                </View>
                <View style={styles.pathText}>
                  <Text style={styles.pathLabel}>{meta.label}</Text>
                  <Text style={styles.pathHint}>
                    {current ? 'Ahora' : done ? 'Listo' : meta.hint}
                  </Text>
                </View>
              </View>
            );
          })}
          {stepTotal > 0 && stepIndex > 0 && !celebrate ? (
            <Text style={styles.pathMeta}>
              Paso {stepIndex} de {stepTotal}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm },
  wrapEmbedded: { gap: 0 },
  card: {
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.xs,
  },
  cardSoft: {
    borderRadius: 14,
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  eyebrow: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    letterSpacing: 0.12,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.85)',
    fontWeight: '700',
  },
  eyebrowSoft: { color: BRAND_INK },
  title: {
    fontFamily: ticketFonts.sans,
    fontSize: 20,
    color: ON_BRAND,
    fontWeight: '800',
  },
  titleSoft: { color: BRAND_INK, fontSize: 17 },
  body: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    lineHeight: 20,
    color: 'rgba(255,255,255,0.92)',
    marginBottom: spacing.sm,
  },
  bodySoft: { color: ticketColors.ink2, marginBottom: spacing.xs },
  cta: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: ticketColors.card,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
  },
  ctaSoft: {
    backgroundColor: BRAND,
    borderRadius: 10,
    paddingVertical: 9,
    paddingHorizontal: 12,
  },
  ctaLabel: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    fontWeight: '700',
    color: BRAND,
  },
  ctaLabelSoft: { color: ON_BRAND },
  after: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: 'rgba(255,255,255,0.75)',
    marginTop: spacing.xs,
  },
  afterSoft: { color: ticketColors.ink3 },
  dismissBtn: { alignSelf: 'flex-start', paddingTop: spacing.sm },
  dismissLabel: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: 'rgba(255,255,255,0.85)',
    textDecorationLine: 'underline',
  },
  path: {
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    padding: spacing.md,
    opacity: 0.92,
  },
  pathEmbedded: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 0,
    opacity: 1,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },
  pathTitle: {
    fontFamily: ticketFonts.sans,
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
    marginBottom: spacing.sm,
  },
  pathRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  pathRowEmbedded: {
    paddingVertical: 10,
    marginBottom: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  pathRowEmbeddedFirst: {
    borderTopWidth: 0,
  },
  check: {
    width: 24,
    height: 24,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: ticketColors.ink3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkDone: {
    backgroundColor: BRAND,
    borderColor: BRAND,
  },
  checkCurrent: {
    borderColor: BRAND,
    backgroundColor: ticketColors.brandSoft,
  },
  checkNum: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
  },
  checkNumOn: { color: BRAND },
  checkNumDone: { color: ON_BRAND },
  pathText: { flex: 1, minWidth: 0 },
  pathLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  pathHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: 2,
  },
  pathMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.xs,
  },
});
